/**
 * pages/Workflows.jsx  —  /workflows
 *
 * Lists all chains in one place:
 *  - Canvas flows  (source:"canvas") — created in FlowBuilder
 *  - Wizard chains (source:"wizard") — created via AddChainWizard
 *
 * Features:
 *  - RunDrawer for execution (shared component)
 *  - New Workflow → FlowBuilder
 *  - Edit canvas flow → FlowBuilder with existing data
 *  - Edit wizard chain → AddChainWizard
 *  - Delete chain
 */

import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Layers, Plus, Pencil, Trash2, Play, Loader2,
  ChevronDown, ChevronRight, GitBranch,
  Search, Sparkles, CalendarClock, MoreVertical,
} from "lucide-react";
import { agentRunAPI } from "../../services/api";
import { useAgents } from "../../context/AgentsContext";
import RunDrawer from "./RunDrawer";
import ScheduleRunModal from "./ScheduleRunModal.jsx";
import {
  EntityCardShell, EntityCardHeader, EntityCardDescription, EntityCardMeta,
  EntityCardMetrics, EntityCardFooter, EntityPrimaryButton, EntityIconButton,
} from "../ui/EntityCard.jsx";
import toast from "react-hot-toast";

// ── Design tokens ─────────────────────────────────────────────────────────────
const T = {
  page:        "var(--lyra-color-surface)",
  surface:     "var(--lyra-color-surface)",
  surface2:    "var(--lyra-color-surface-2)",
  surface3:    "var(--app-surface-3)",
  border:      "var(--lyra-color-border)",
  border2:     "var(--app-border-strong)",
  ink:         "var(--lyra-color-text)",
  ink2:        "var(--app-text-2)",
  ink3:        "var(--lyra-color-text-muted)",
  ink4:        "var(--app-text-3)",
  accent:      "var(--lyra-color-primary)",
  accentLight: "var(--app-primary-soft)",
  accentMid:   "var(--app-primary-soft-2)",
  green:       "var(--lyra-color-success)",
  greenBg:     "var(--lyra-color-success-bg)",
  greenBd:     "var(--app-success-border)",
  greenText:   "#14532D",
  violet:      "var(--lyra-color-primary)",
  violetBg:    "var(--app-primary-soft)",
  violetBd:    "var(--app-primary-soft-2)",
  violetText:  "var(--app-primary-deep)",
  red:         "var(--lyra-color-danger)",
  redBg:       "var(--lyra-color-danger-bg)",
  redBd:       "var(--app-danger-border)",
  amber:       "var(--lyra-color-warning)",
  amberBg:     "var(--lyra-color-warning-bg)",
  amberBd:     "var(--app-warning-border)",
};

const GLOBAL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600&display=swap');
  @keyframes spin   { to { transform: rotate(360deg); } }
  @keyframes fadeIn { from { opacity:0; transform:translateY(4px); } to { opacity:1; transform:translateY(0); } }
  * { box-sizing: border-box; }
  ::-webkit-scrollbar { width: 4px; }
  ::-webkit-scrollbar-thumb { background: ${T.border}; border-radius: 4px; }
`;

// ── Section header ────────────────────────────────────────────────────────────
function SectionHead({ title, count, action, collapsed, onToggle }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
      <span style={{ fontSize: 13, fontWeight: 600, color: T.ink2 }}>{title}</span>
      {count !== undefined && (
        <span style={{ fontSize: 11, fontWeight: 500, padding: "2px 8px", borderRadius: 99, background: T.surface2, color: T.ink3, border: `1px solid ${T.border}` }}>{count}</span>
      )}
      <div style={{ flex: 1 }} />
      {action}
      {onToggle && (
        <button onClick={onToggle}
          style={{ width: 26, height: 26, borderRadius: 7, border: `1px solid ${T.border}`, background: T.surface2, cursor: "pointer", color: T.ink3, display: "flex", alignItems: "center", justifyContent: "center", marginLeft: 4 }}
          onMouseEnter={e => e.currentTarget.style.background = T.surface3}
          onMouseLeave={e => e.currentTarget.style.background = T.surface2}>
          {collapsed ? <ChevronRight size={13} /> : <ChevronDown size={13} />}
        </button>
      )}
    </div>
  );
}

// ── Workflow Card ─────────────────────────────────────────────────────────────
function WorkflowCard({ chain, agents, onRun, onEdit, onDelete, onSchedule, scheduledRun, isMobile = false }) {
  const isCanvas   = chain.source === "canvas";
  const nodes      = chain.canvasData?.nodes      || [];
  const steps      = chain.steps || [];
  const stepCount  = isCanvas ? nodes.filter(n => n.type === "agent").length : steps.length;
  const typeLabel  = isCanvas ? "Visual flow" : "Wizard chain";
  const TypeIcon   = isCanvas ? GitBranch : Layers;
  const [menuOpen, setMenuOpen] = useState(false);

  const menu = (
    <div style={{ position: "relative" }}>
      <button onClick={() => setMenuOpen(v => !v)} title="Workflow actions" aria-label="Workflow actions"
        style={{ width: 32, height: 32, borderRadius: 8, border: `1px solid ${T.border}`, background: T.surface, color: T.ink3, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
        <MoreVertical size={16} />
      </button>
      {menuOpen && (
        <>
          <div onClick={() => setMenuOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 20 }} />
          <div style={{ position: "absolute", right: 0, top: 38, zIndex: 21, width: 178, padding: 6, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10, boxShadow: "0 10px 30px rgba(15,23,42,.14)" }}>
            <button onClick={() => { setMenuOpen(false); onSchedule(chain); }}
              style={{ width: "100%", border: "none", borderRadius: 7, padding: "8px 9px", background: "transparent", color: T.ink2, cursor: "pointer", display: "flex", alignItems: "center", gap: 8, fontFamily: "inherit", fontSize: 12, textAlign: "left" }}>
              <CalendarClock size={14} /> {scheduledRun ? "Reschedule" : "Schedule run"}
            </button>
            <button onClick={() => { setMenuOpen(false); onDelete(chain); }}
              style={{ width: "100%", border: "none", borderRadius: 7, padding: "8px 9px", background: "transparent", color: T.red, cursor: "pointer", display: "flex", alignItems: "center", gap: 8, fontFamily: "inherit", fontSize: 12, textAlign: "left" }}>
              <Trash2 size={14} /> Delete workflow
            </button>
          </div>
        </>
      )}
    </div>
  );

  return (
    <EntityCardShell>
      <EntityCardHeader
        icon={TypeIcon}
        eyebrow="Workflow"
        title={chain.name}
        status={scheduledRun ? "Scheduled" : "Active"}
        menu={menu}
      />
      <EntityCardDescription>
        {chain.description || (isCanvas ? "Automates a multi-step canvas flow across connected agents." : "Coordinates a reusable sequence of connected AI agents.")}
      </EntityCardDescription>
      <EntityCardMeta icon={TypeIcon}>{typeLabel} · {stepCount} {isCanvas ? "nodes" : "steps"}</EntityCardMeta>
      <EntityCardMetrics items={[
        { value: chain.runCount || 0, label: "Runs" },
        { value: chain.lastRunAt ? new Date(chain.lastRunAt).toLocaleDateString([], { day: "2-digit", month: "short" }) : "Never", label: "Last run" },
        { value: new Date(chain.createdAt || Date.now()).toLocaleDateString([], { day: "2-digit", month: "short" }), label: "Created" },
      ]} />
      <EntityCardFooter>
        <EntityPrimaryButton onClick={() => onRun(chain)}><Play size={14} fill="currentColor" /> Run workflow</EntityPrimaryButton>
        <EntityIconButton onClick={() => onEdit(chain)} title="Edit workflow"><Pencil size={16} /></EntityIconButton>
      </EntityCardFooter>
    </EntityCardShell>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function Workflows() {
  const navigate = useNavigate();
  const { agents, allChains, loadingChains, loadChains, handleChainSaved, deleteChain } = useAgents();

  const [runningChain,  setRunningChain]  = useState(null);
  const [searchQ,       setSearchQ]       = useState("");
  const [canvasOpen,    setCanvasOpen]    = useState(true);
  const [wizardOpen,    setWizardOpen]    = useState(true);
  const [schedulingChain, setSchedulingChain] = useState(null);
  const [scheduledByChain, setScheduledByChain] = useState({});
  const [isMobile, setIsMobile] = useState(() => (typeof window !== 'undefined' ? window.innerWidth <= 900 : false));
  const [pageLoading, setPageLoading] = useState(true);
  const didRefreshChains = useRef(false);

  // The provider loads app-wide data while the route is mounting.  Keep this
  // page in a loading state until it has observed one completed refresh, so a
  // transient empty array can never be presented as "No workflows yet".
  useEffect(() => {
    if (loadingChains) return;
    if (didRefreshChains.current) { setPageLoading(false); return; }
    didRefreshChains.current = true;
    setPageLoading(true);
    loadChains().catch(() => {}).finally(() => setPageLoading(false));
  }, [loadingChains, loadChains]);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 900);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const loadScheduled = useCallback(() => {
    agentRunAPI.getScheduled().then(d => {
      const map = {};
      (d?.runs || []).forEach(r => { if (r.chainId) map[String(r.chainId)] = r; });
      setScheduledByChain(map);
    }).catch(() => setScheduledByChain({}));
  }, []);

  useEffect(() => { loadScheduled(); }, [loadScheduled]);

  const canvasFlows  = (allChains || []).filter(c => c.source === "canvas");
  const wizardChains = (allChains || []).filter(c => c.source !== "canvas");
  const workflowsLoading = loadingChains || pageLoading;

  const filteredCanvas = canvasFlows.filter(c =>
    c.name.toLowerCase().includes(searchQ.toLowerCase())
  );
  const filteredWizard = wizardChains.filter(c =>
    c.name.toLowerCase().includes(searchQ.toLowerCase())
  );

  const handleEdit = chain => {
    const chainId = chain._id || chain.id;
    // Keep the selected workflow in the URL so Edit works after a refresh too.
    navigate(`/flow-builder?flow=${encodeURIComponent(chainId)}`, { state: { existingFlow: chain } });
  };

  const handleDelete = async chain => {
    if (!window.confirm(`Delete "${chain.name}"? This cannot be undone.`)) return;
    const ok = await deleteChain(chain._id || chain.id);
    if (ok) {
      if (runningChain?._id === chain._id) setRunningChain(null);
      toast.success("Workflow deleted");
    } else {
      toast.error("Failed to delete workflow");
    }
  };

  const isEmpty = canvasFlows.length === 0 && wizardChains.length === 0;

  return (
    <div style={{ minHeight: "100vh", background: T.page, fontFamily: "DM Sans, system-ui, sans-serif" }}>
      <style>{GLOBAL_CSS}</style>
      <div style={{ maxWidth: 1280, margin: "0 auto", padding: isMobile ? "14px 12px 40px" : "28px 32px 60px" }}>

      {/* Run drawer */}
      {runningChain && (
        <RunDrawer
          target={runningChain}
          targetType="chain"
          onClose={() => setRunningChain(null)}
          agents={agents}
        />
      )}

      {/* Schedule modal — same one-time AgentRun primitive used for agents,
          so a scheduled workflow shows up on the Schedule page for free. */}
      {schedulingChain && (
        <ScheduleRunModal
          target={schedulingChain}
          targetType="chain"
          existingRun={scheduledByChain[String(schedulingChain._id)]}
          onClose={() => setSchedulingChain(null)}
          onScheduled={() => loadScheduled()}
          onCancelExisting={() => loadScheduled()}
        />
      )}

      {/* Page header */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 10, marginBottom: 18 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 600, color: T.ink, marginBottom: 4 }}>Workflows{workflowsLoading ? "" : ` (${canvasFlows.length})`}</div>
          <div style={{ fontSize: 13, color: T.ink3 }}>Multi-agent pipelines and automated sequences</div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", width: isMobile ? "100%" : "auto" }}>
          {/* Search */}
          <div style={{ display: "flex", alignItems: "center", gap: 6, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 8, padding: "6px 11px", width: isMobile ? "100%" : 180 }}>
            <Search size={12} color={T.ink4} />
            <input value={searchQ} onChange={e => setSearchQ(e.target.value)} placeholder="Search workflows…"
              style={{ border: "none", background: "transparent", outline: "none", fontSize: 12, color: T.ink, width: "100%", fontFamily: "inherit" }} />
          </div>
          {/* Visual flow */}
          <button onClick={() => navigate("/flow-builder")}
            style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 15px", borderRadius: 9, background: T.accent, color: "var(--app-on-accent)", border: "none", fontSize: 12.5, fontWeight: 500, cursor: "pointer", fontFamily: "inherit", width: isMobile ? "100%" : "auto", justifyContent: isMobile ? "center" : "flex-start" }}>
            <Plus size={14} /> Visual Flow
          </button>
          <button onClick={() => navigate("/dashboard")}
            style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 15px", borderRadius: 9, background: T.surface2, color: T.ink2, border: `1px solid ${T.border}`, fontSize: 12.5, fontWeight: 500, cursor: "pointer", fontFamily: "inherit", width: isMobile ? "100%" : "auto", justifyContent: "center" }}>
            <Sparkles size={13} /> Create from chat
          </button>
        </div>
      </div>

      {/* Loading */}
      {workflowsLoading && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, color: T.ink3, padding: "40px 0", fontSize: 13, justifyContent: "center" }}>
          <Loader2 size={18} style={{ animation: "spin 1s linear infinite" }} /> Loading workflows…
        </div>
      )}

      {/* Empty state */}
      {!workflowsLoading && isEmpty && (
        <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: "52px 20px", textAlign: "center" }}>
          <Layers size={36} color={T.ink4} style={{ margin: "0 auto 14px" }} />
          <div style={{ fontSize: 15, fontWeight: 600, color: T.ink2, marginBottom: 6 }}>No workflows yet</div>
          <div style={{ fontSize: 13, color: T.ink3, marginBottom: 24 }}>
            Describe an automation in Home, or create a precise visual flow here.
          </div>
          <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
            <button onClick={() => navigate("/dashboard")}
              style={{ display: "flex", alignItems: "center", gap: 7, padding: "10px 22px", borderRadius: 9, background: T.violet, color: "var(--app-on-accent)", border: "none", fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "inherit" }}>
              <Sparkles size={14} /> Create from chat
            </button>
            <button onClick={() => navigate("/flow-builder")}
              style={{ display: "flex", alignItems: "center", gap: 7, padding: "10px 22px", borderRadius: 9, background: T.surface2, color: T.ink2, border: `1px solid ${T.border}`, fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "inherit" }}>
              <Plus size={14} /> Visual Flow
            </button>
          </div>
        </div>
      )}


      {/* ── Canvas flows section ── */}
      {!workflowsLoading && canvasFlows.length > 0 && (
        <div style={{ marginBottom: wizardChains.length > 0 ? 26 : 0 }}>
          <SectionHead
            title="Visual Flows"
            count={canvasFlows.length}
            collapsed={!canvasOpen}
            onToggle={() => setCanvasOpen(v => !v)}
          />
          {canvasOpen && (
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fill, minmax(320px, 1fr))", gap: 12, animation: "fadeIn .18s ease" }}>
              {filteredCanvas.map(chain => (
                <WorkflowCard key={chain._id} chain={chain} agents={agents} onRun={setRunningChain} onEdit={handleEdit} onDelete={handleDelete} onSchedule={setSchedulingChain} scheduledRun={scheduledByChain[String(chain._id)]} isMobile={isMobile} />
              ))}
              {filteredCanvas.length === 0 && searchQ && (
                <div style={{ color: T.ink4, fontSize: 13, padding: "20px 0" }}>No visual flows match "{searchQ}"</div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Wizard chains section ── */}
      {!workflowsLoading && wizardChains.length > 0 && (
        <div>
          <SectionHead
            title="Existing workflows"
            count={wizardChains.length}
            collapsed={!wizardOpen}
            onToggle={() => setWizardOpen(v => !v)}
            action={
              <button onClick={() => navigate("/dashboard")}
                style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, color: T.violet, background: T.violetBg, border: `1px solid ${T.violetBd}`, borderRadius: 7, padding: "5px 11px", cursor: "pointer", fontFamily: "inherit", fontWeight: 500 }}>
                <Sparkles size={12} /> Create in Home
              </button>
            }
          />
          {wizardOpen && (
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fill, minmax(320px, 1fr))", gap: 12, animation: "fadeIn .18s ease" }}>
              {filteredWizard.map(chain => (
                <WorkflowCard key={chain._id} chain={chain} agents={agents} onRun={setRunningChain} onEdit={handleEdit} onDelete={handleDelete} onSchedule={setSchedulingChain} scheduledRun={scheduledByChain[String(chain._id)]} isMobile={isMobile} />
              ))}
              {filteredWizard.length === 0 && searchQ && (
                <div style={{ color: T.ink4, fontSize: 13, padding: "20px 0" }}>No workflows match "{searchQ}"</div>
              )}
            </div>
          )}
        </div>
      )}
      </div>
    </div>
  );
}
